import { escapeHtml } from '../inventoree/format'
import type { TableColumn } from '../ui/DataTable'

export type NamedColumn = TableColumn & { name?: string }

export function servicesColumns(): NamedColumn[] {
  return [
    {
      index: 0, title: '', orderable: false, visible: true, width: 50,
      html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${escapeHtml(r.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1, title: 'Service type', name: 'service_name', orderable: true, visible: true,
      html: (r) => r.service_name == null ? '<span class="color-red">HOST</span>' : escapeHtml(r.service_name),
    },
    {
      index: 2, title: 'Critical', name: 'critical', orderable: true, visible: true,
      html: (r) => `<span class="label label-danger"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> ${escapeHtml(r.critical ?? 0)}</span>`,
    },
    {
      index: 3, title: 'Warning', name: 'warning', orderable: true, visible: true,
      html: (r) => `<span class="label label-warning"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> ${escapeHtml(r.warning ?? 0)}</span>`,
    },
    {
      index: 4, title: 'Up', name: 'up', orderable: true, visible: true,
      html: (r) => `<span class="label label-primary"><i class="fa fa-check" aria-hidden="true"></i> ${escapeHtml(r.up ?? 0)}</span>`,
    },
    {
      index: 5, title: 'Last outage date', name: 'last_outage_date', orderable: true, visible: true,
      html: (r) => r.last_outage_date == null ? '<span>n/a</span>' : `<span>${escapeHtml(r.last_outage_date)}</span>`,
    },
  ]
}

export function hostsColumns(): NamedColumn[] {
  return [
    {
      index: 0, title: '', orderable: false, visible: true, width: 50,
      html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${escapeHtml(r.deviceId)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1, title: 'Type', name: 'it_echipamente.Tip', orderable: true, visible: true, width: 50,
      html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${String(r.device_icon ?? '')}</center>`,
    },
    {
      index: 2, title: 'Host name', name: 'it_echipamente.Denumire', orderable: true, visible: true,
      html: (r) => {
        /* Aparat urmarit de dinainte, al carui tip nu mai are bifa "Remote
           Monitoring" in Device types: NU se opreste, doar se spune. */
        const off = r.type_remote_monitoring === false
          ? ' <span class="label label-warning od-rm-off" data-toggle="tooltip" data-placement="top"'
            + ' title="This device type is not enabled for Remote Monitoring in Settings &gt; Inventoree &gt; Device types.'
            + ' The device is still watched; new devices of this type cannot be added.">Type not enabled</span>'
          : ''
        const name = `<p>${escapeHtml(r.Denumire)}${off}</p>`
        if (Number(r.ownerId) > 0) {
          return name + `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(r.Prenume)} ${escapeHtml(r.Nume)}</span>`
        }
        return name + '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'
      },
    },
    {
      index: 3, title: 'Agent IP address', name: 'ip', orderable: true, visible: true,
      html: (r) => r.ip == null || r.ip === '' ? '' : escapeHtml(r.ip),
    },
    {
      index: 4, title: 'UDP port', name: 'udp_port', orderable: true, visible: true,
      html: (r) => r.udp_port == null || Number(r.udp_port) === 0 ? '' : escapeHtml(r.udp_port),
    },
    {
      index: 5, title: 'Status', name: 'status', orderable: true, visible: true,
      html: (r) => {
        if (r.status == 0 || r.status == null) {
          return '<center data-toggle="tooltip" data-placement="top" title="UP"><i class="fas fa-check-circle font-20 color-green"></i></center>'
        }
        return '<center data-toggle="tooltip" data-placement="top" title="DOWN"><i class="fa fa-exclamation-triangle font-18 color-red"></i></center>'
      },
    },
    {
      index: 6, title: 'Last outage date', name: 'outage_date', orderable: true, visible: true,
      html: (r) => r.outage_date == null ? '<em>n/a</em>' : escapeHtml(r.outage_date),
    },
    {
      index: 7, title: 'Virtual machines', name: 'vm', orderable: true, visible: true,
      html: (r) => r.vm == null || Number(r.vm) === 0
        ? '<span class="label label-info">0</span>'
        : `<span class="label label-primary">${escapeHtml(r.vm)}</span>`,
    },
    {
      index: 8, title: 'Virtual machines Down', name: 'vm_down', orderable: true, visible: true,
      html: (r) => r.vm_down == null || Number(r.vm_down) === 0
        ? '<span class="label label-info">0</span>'
        : `<span class="label label-danger">${escapeHtml(r.vm_down)}</span>`,
    },
  ]
}

export function coreAppColumns(): NamedColumn[] {
  return [
    {
      index: 0, title: '', orderable: false, visible: true, width: 50,
      html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${escapeHtml(r.application_id)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1, title: 'Application Name', name: 'application_name', orderable: true, visible: true,
      html: (r) => escapeHtml(r.application_name),
    },
    {
      index: 2, title: 'Devices', name: 'devices', orderable: true, visible: true, width: 100,
      html: (r) => Number(r.devices) > 0
        ? `<center><span class="label label-primary">${escapeHtml(r.devices)}</span></center>`
        : `<center><span class="label label-default">${escapeHtml(r.devices)}</span></center>`,
    },
    {
      index: 3,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allInputs"/></center>',
      orderable: false,
      visible: true,
      width: 100,
      html: (r) => `<center><input value="${escapeHtml(r.application_id)}" type="checkbox" class="oneInput"/></center>`,
    },
  ]
}
